import * as yaml from 'js-yaml'
import * as z from 'zod'

import {
  default as surveyListSchema,
  type SurveyList,
} from './schemas/surveyList'

export type ValidResult = {
  isValid: true
  errors: []
  data: SurveyList
}

export type InvalidResult = {
  isValid: false
  errors: ParsingError[]
  data?: undefined
}

export type ParsingResult = ValidResult | InvalidResult

export type ParsingError = z.core.$ZodIssue

function flattenZodIssues(zodError: z.ZodError): ParsingError[] {
  const result: ParsingError[] = []
  for (const issue of zodError.issues) {
    result.push(issue as ParsingError)
    const withUnion = issue as { unionErrors?: z.ZodError[] }
    if (Array.isArray(withUnion.unionErrors)) {
      for (const err of withUnion.unionErrors) {
        result.push(...flattenZodIssues(err))
      }
    }
  }
  return result
}

const invalidResponse = (message: string): ParsingResult => ({
  isValid: false,
  errors: [{ code: 'custom', path: [], message }],
})

const parseSurveyListConfig = (yamlString: string): ParsingResult => {
  if (!yamlString.trim()) {
    return invalidResponse('YAML content cannot be empty')
  }

  let parsed: unknown
  try {
    parsed = yaml.load(yamlString)
  } catch (error) {
    const yamlError = error as yaml.YAMLException
    return invalidResponse(yamlError.message || 'Failed to parse YAML')
  }

  if (!parsed) {
    return invalidResponse('Invalid YAML structure')
  }

  const { success, data, error } = surveyListSchema.safeParse(parsed)
  if (!success) {
    return { isValid: false, errors: flattenZodIssues(error) }
  }

  return { isValid: true, errors: [], data }
}

export default parseSurveyListConfig
